import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
} from '@tencent/spaui';
import { UserType } from './constant';
import UserTypeTip from './user-type-tip';
import { createHelpUrl } from './utils/qidian';
import { observer } from 'mobx-react';
import globalStore from '@/stores';
import { hasFeatureFlag } from '@/services';
import { getBusinessList, getUnassignedAccountInfoList, getUserCert } from './actions';

import  './index.less';

const STORAGE_KEY_PICKED_TYPE = 'auth_user_type_picked';
const STORAGE_KEY_CLOSED = 'auth_user_type_closed';

// 选择用户类型的弹窗
const AuthUserTypeDialog: React.FC<any> = observer((props) => {
  const userId = globalStore.account?.user_id;
  const real_name_verify_status = globalStore.account?.real_name_verify_status;
  const [pickedType, setPickedType] = useState<UserType | null>(localStorage.getItem(STORAGE_KEY_PICKED_TYPE) as UserType || null);
  const [closed, setClosed] = useState(Boolean(sessionStorage.getItem(STORAGE_KEY_CLOSED)));
  const [hasCert, setHasCert] = useState(true);
  const allowShow = Boolean(userId)
    && hasFeatureFlag('is_enable_strict_employee_rule')
    && hasFeatureFlag('is_user_auth_popup_enable');
  const shouldShow = allowShow && (!hasCert || !real_name_verify_status);
  const entryShow = useMemo(() => shouldShow && !pickedType && !closed, [shouldShow, pickedType, closed]);
  const itemShow = useMemo(() => shouldShow && Boolean(pickedType) && !closed, [shouldShow, pickedType, closed]);

  // 是否有工作台
  const [hasWorktable, setHasWorktable] = useState(false);
  // 是否有投放端
  const [hasAd, setHasAd] = useState(false);

  const initUserAccountInfo = useCallback(async () => {
    // 用户认证主体获取
    getUserCert({
      user_id: userId,
    }).then((res) => {
      setHasCert(Boolean(res.data?.list?.length));
    });

    // 是否有工作台
    getBusinessList({
      user_id: userId,
    }).then((res) => {
      setHasWorktable(Boolean(res.data?.list?.length));
    });

    // 是否有投放端
    getUnassignedAccountInfoList({
      user_id: userId,
    }).then((res) => {
      setHasAd(Boolean(res.data?.length));
    });
  }, []);

  useEffect(() => {
    if (allowShow) {
      initUserAccountInfo();
    }
  }, [allowShow]);

  const handleChangeType = useCallback((type) => {
    setPickedType(type);
    localStorage.setItem(STORAGE_KEY_PICKED_TYPE, type);
  }, [setPickedType]);

  // 重新选择身份
  const repickType = useCallback(() => {
    setPickedType(null);
    localStorage.removeItem(STORAGE_KEY_PICKED_TYPE);
  }, [setPickedType]);

  const onCloseEntry = useCallback(() => {
    sessionStorage.setItem(STORAGE_KEY_CLOSED, '1');
    setClosed(true);
    setPickedType(null);
  }, [setPickedType]);

  const onOpenAuth = useCallback(() => {
    setClosed(false);
  }, []);

  // dialog 内容
  const dialogContent = useMemo(() => {
    if (pickedType === UserType.AGENCY) {
      return '<p>用户您好：为了您的账号使用安全，避免影响账号正常使用，请您尽快联系您的管理员，完成此账号的实名认证操作，如果有问题请联系您所属服务商的系统管理员。感谢您的配合。</p>';
    }
    if (pickedType === UserType.PARTNER) {
      return '<p>用户您好：为了您的账号使用安全，避免影响账号正常使用，请您尽快联系对应合作伙伴的管理员完成相关实名认证操作，如有问题请联系您所在合作伙伴的系统管理员。感谢您的配合。</p>';
    }
    if (pickedType === UserType.ADVERTISER) {
      return '<p>用户您好：为了您的账号使用安全，避免影响账号正常使用，请您尽快按照以下指引，在广告主工作台完成实名认证操作。感谢您的配合。</p>';
    }
    if (pickedType === UserType.ELSE) {
      return '<p>用户您好：为了您的账号使用安全，避免影响账号正常使用，请您尽快联系渠道接口人完成对应操作。感谢您的配合。</p>';
    }
    return '';
  }, [pickedType]);

  // dialog 按钮
  const dialogButtons = useMemo(() => {
    if (pickedType === UserType.AGENCY) {
      return {
        buttons: [{
          text: '查看操作指引',
          link: 'https://doc.weixin.qq.com/doc/w3_AbsAHQafAIQorBt0y1bQIuSy6126R',
        }],
      };
    }
    if (pickedType === UserType.PARTNER) {
      return {
        buttons: [{
          text: '查看操作指引',
          link: 'https://doc.weixin.qq.com/doc/w3_AbsAHQafAIQorBt0y1bQIuSy6126R',
        }],
      };
    }
    if (pickedType === UserType.ADVERTISER) {
      return {
        buttons: [
          {
            text: '查看操作指引',
            link: 'https://docs.qq.com/doc/DWWtQQVdXTUJ3Vmhp',
          },
          {
            text: '去工作台',
            link: hasWorktable
              ? 'https://ad.qq.com/worktable/' // 工作台
              : hasAd
                ? 'https://ad.qq.com/' // 投放端首页
                : 'https://e.qq.com/technology/steward/', // 新建商务管家页面
          },
        ],
      };
    }
    if (pickedType === UserType.ELSE) {
      return {
        buttons: [{
          text: '在线咨询',
          link: createHelpUrl({
            id: 'uiUGNlLc',
            userId,
            uriPath: `${location.origin}${location.pathname}#userAuthDialog`,
          }),
        }],
      };
    }
  }, [pickedType, userId, hasWorktable, hasAd]);

  return <>
    {
      shouldShow
        ? <span style={{ marginLeft: '15px' }}>请您尽快完成账号认证！ <a onClick={onOpenAuth} className='user-auth-link'>前往认证</a></span>
        : null
    }
    <Dialog
      useNewUI
      type="normal"
      onClose={onCloseEntry}
      onSubmit={onCloseEntry}
      closeButton={true}
      cancelButton={false}
      style={{ width: '800px' }}
      show={entryShow}
      title="身份验证提示"
      submitLabel="暂不验证"
    >
      <AuthUserTypeContent handleChangeType={handleChangeType}/>
    </Dialog>
    <CommonWarnDialog
      show={itemShow}
      userType={pickedType as unknown as UserType}
      repickType={repickType}
      content={dialogContent}
      {...dialogButtons as any}
      onClose={onCloseEntry}
    />
  </>;
});

interface AuthUserTypeContentProps {
  handleChangeType: (type: string) => void;
}

const AuthUserTypeContent: React.FC<AuthUserTypeContentProps> = (props) => {
  const {
    handleChangeType,
  } = props;

  return <div className='auth-user-type-picker'>
    {
      <div style={{ lineHeight: '2.2em' }}>
        <p>您好，为保障账号使用安全，请您按照以下指引确认身份角色，完成对应身份的后续操作</p>
      </div>
    }
    <h3 style={{ marginTop: '15px' }}>您的身份角色是？</h3>
    <div className='items'>
      <div className='item' onClick={() => handleChangeType(UserType.AGENCY)}>服务商员工</div>
      <div className='item' onClick={() => handleChangeType(UserType.PARTNER)}>合作伙伴员工</div>
      <div className='item' onClick={() => handleChangeType(UserType.ADVERTISER)}>广告主员工</div>
      <div className='item' onClick={() => handleChangeType(UserType.ELSE)}>以上都不是</div>
    </div>
  </div>;
};

export default React.memo(AuthUserTypeDialog);

interface CommonWarnDialogProps {
  show: boolean;
  userType: UserType;
  repickType: () => void;
  content: string;
  buttons: {
    text: string;
    link: string;
  }[];
  onClose: () => void;
}

// 通用弹窗
const CommonWarnDialog: React.FC<CommonWarnDialogProps> = (props) => {
  const {
    show,
    userType,
    repickType,
    content,
    buttons, // text, link
    onClose,
  } = props;

  const jumpLink = useCallback((index: number) => {
    window.open(buttons[index]?.link);
  }, [buttons]);

  return <Dialog
    useNewUI
    show={show}
    type="normal"
    onClose={onClose}
    onSubmit={onClose}
    closeButton={true}
    cancelButton={false}
    style={{ width: '600px' }}
    title="身份验证提示"
    submitLabel="暂不验证"
  >
    <div className='user-type-picker-common-content'>
      <UserTypeTip userType={userType} repickType={repickType} />
      <div className='content' dangerouslySetInnerHTML={{ __html: content }}/>
      {
        buttons?.length
          ? <div className='buttons'>
            {
              buttons.map((button, index) => {
                return <Button key={button.text} className='main-button' displayType='primary' onClick={() => jumpLink(index)}>{ button?.text }</Button>;
              })
            }
          </div>
          : null
      }
    </div>
  </Dialog>;
};

